import React from 'react';
import I18n from 'i18n-js';
import { withRouter, WithRouterProps } from '@reverbdotcom/commons/src/withRouter';

import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { ThumbnailType } from '../elog/mparticle_types';
import LightboxImageGallery from './lightbox_image_gallery';
import LightboxImageViewer from './lightbox_image_viewer';
import LightboxImageStageViewer from './lightbox_image_stage_viewer';
import { LightboxIndexChangeSource, isLink } from './lightbox_image_parts';

export interface ExternalProps {
  thumbURLs?: string[];
  imageURLs?: string[];
  imageAlts?: string[];
  fullsizeURLs: string[];
  withControls?: boolean;
  // size of the prev/next controls on the primary image; the modal's controls are always medium
  controlSize?: 'small' | 'medium';
  id?: string;
  // names the full-screen viewer for screen readers, e.g. the listing or product title
  label?: string;
  captionText?: string;
  landscapeIfVideo?: boolean;
  showAltsAsCaptions?: boolean;
  persistKeystrokes?: boolean;
  useAspectOverride?: boolean;
  disableFullScreen?: boolean;
  largerThumbs?: boolean;

  // renders images in a grid layout
  isImageGrid?: boolean;
  // overrides button displayed in grid layout
  imageGridButton?: React.ReactNode;

  parentComponentName?:string;
  bumpInteractionTracking?: (componentName: string) => void;

  // hides thumbnails but shows them in the modal itself
  hideThumbs?: boolean;

  // hides primary image container
  hidePrimary?: boolean;

  // Eager loads primary image. This is important for Largest Contentful Paint (LCP) performance.
  // Generally, you should elect to lazy load images. However, the largest image on the page should
  // be eager loaded, especially when it is above the fold.
  eagerLoadPrimaryImage?: boolean;

  // Disables RCImage's overlay.
  disableImageOverlay?: boolean;

  // which full-screen viewer opens: today's, or the stage with a thumbnail rail
  viewer?: 'classic' | 'stage';
}

type IProps = ExternalProps & WithRouterProps;

const INTERACTION_NAMES: Record<LightboxIndexChangeSource, string> = {
  next: 'nextImage',
  previous: 'prevImage',
  thumbnail: 'thumbImage',
};

// An inline gallery of images that opens a full-screen viewer. It owns the index the two share.
export function LightboxImage({
  thumbURLs,
  imageURLs,
  imageAlts,
  fullsizeURLs,
  id,
  label,
  captionText,
  parentComponentName,
  bumpInteractionTracking,
  disableImageOverlay,
  viewer = 'classic',
  ...galleryProps
}: IProps) {
  const [index, setIndex] = React.useState(0);
  const [isOpen, setIsOpen] = React.useState(false);

  if (!fullsizeURLs?.length) { return null; }

  function handleIndexChange(newIndex: number, { source }: { source: LightboxIndexChangeSource }) {
    setIndex(newIndex);

    if (source === 'thumbnail') {
      trackEvent({
        eventName: MParticleEventName.ClickedItemImageThumbnail,
        position: newIndex + 1,
        imageCount: thumbURLs.length,
        inModal: isOpen,
        thumbnailType: isLink(thumbURLs[newIndex]) ? ThumbnailType.Image : ThumbnailType.Video,
      });
    }

    bumpInteractionTracking?.(`${parentComponentName}:${INTERACTION_NAMES[source]}`);
  }

  function handleOpenViewer({ source }: { source: 'mainImage' | 'thumbnail' }) {
    setIsOpen(true);

    if (source === 'mainImage') {
      bumpInteractionTracking?.(`${parentComponentName}:mainImage`);
    }
  }

  function handleOpenChange(open: boolean) {
    if (!open) setIsOpen(false);
  }

  const Viewer = viewer === 'stage' ? LightboxImageStageViewer : LightboxImageViewer;

  return (
    <LightboxImageGallery
      {...galleryProps}
      thumbURLs={thumbURLs}
      imageURLs={imageURLs}
      imageAlts={imageAlts}
      fullsizeURLs={fullsizeURLs}
      disableImageOverlay={disableImageOverlay}
      index={index}
      onIndexChange={handleIndexChange}
      onOpenViewer={handleOpenViewer}
      isViewerOpen={isOpen}
    >
      <Viewer
        items={fullsizeURLs.map((src, i) => ({
          src,
          thumbnail: thumbURLs?.[i],
          preview: imageURLs?.[i],
          alt: imageAlts?.[i],
        }))}
        index={index}
        onIndexChange={handleIndexChange}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        label={label || I18n.t('commons.lightboxImage.label')}
        id={id}
        caption={captionText}
        disableImageOverlay={disableImageOverlay}
      />
    </LightboxImageGallery>
  );
}

export default withRouter<ExternalProps>(LightboxImage);
